<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2
        id="childhood-syndromes-test-title"
        style="font-size: 24px; display: flex; flex-wrap: wrap; gap: 8px"
      >
        <span>Childhood syndrome </span><span>- diagnostic exome (saliva)</span
        ><span> or WGS (EDTA blood)</span>
      </h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="childhood-syndrome-test-grid"
      >
        <v-container>
          <v-row>
            <v-col class="custom-col">
              <p>
                <a
                  href="https://www.vcgs.org.au/tests/childhood-exome/"
                  target=""
                  _blank
                  >Exome or Whole genome sequencing</a
                >
                can be used in cases where a child has intellectual disability
                and/or developmental delay and where an explanation has not been
                found from previous testing, such as a chromosome microarray.
                MBS item numbers
                <a
                  href="https://www9.health.gov.au/mbs/fullDisplay.cfm?type=item&q=73358&qt=ItemID"
                  target="_blank"
                  >73358</a
                >
                (singleton) and
                <a
                  target="_blank"
                  href="https://www9.health.gov.au/mbs/fullDisplay.cfm?type=item&q=73359&qt=ItemID"
                  >73359</a
                >
                (trio) exist for exome or WGS testing for childhood syndromes
                and intellectual disability.
              </p>
              <p>
                Performed only after non-informative microarray testing
                <a
                  target="_blank"
                  href="https://www9.health.gov.au/mbs/fullDisplay.cfm?type=item&q=73292&qt=item&criteria=73292"
                  >(item# 73292)</a
                >
                of the patient.
              </p>
              <p>
                If your patient does not meet these Medicare criteria, please
                contact
                <a href="mailto:VCGSgenomics@vcgs.org.au"
                  >VCGSgenomics@vcgs.org.au</a
                >
                to discuss alternate test options.
              </p>
              <p>
                If all required samples received are EDTA blood, testing will
                proceed via Whole Genome Sequencing.
              </p>
            </v-col>
          </v-row>
          <v-row id="medicare-inclusion-criteria">
            <v-col class="custom-col">
              <h3 class="mb-2 red--text ">
                Medicare item number 73358 & 73359 inclusion criteria*
              </h3>
              <div class="mb-2">
                Characterisation of germline variants known to cause monogenic
                disorders via whole exome or genome sequencing and analysis, as
                requested by a clinical geneticist or a consultant physician
                practising as a specialist paediatrician in consultation with a
                clinical geneticist in a patient aged ten years or younger (ie
                prior to their 11th birthday) with a strong suspicion of a
                monogenic condition based on the presence of any one of the two
                following clinical criteria:
              </div>
              <ol>
                <li>
                  dysmorphic facial appearance AND one or more major structural
                  congenital anomalies
                </li>
                <div>OR</div>
                <li>
                  intellectual disability or global developmental delay of at
                  least moderate severity to be determined by a specialist
                  paediatrician.
                </li>
              </ol>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="1">
              <v-checkbox
                v-model="acknowledgeCriteria"
                :id="testId + '.criteriaCheckbox'"
                hide-details
              />
            </v-col>
            <v-col cols="11">
              <span style="display: inline-block;" class="ml-md-n4 ml-xl-n16">
                By checking this box, I acknowledge that the patient meets the
                above clinical criteria
              </span>
            </v-col>
            <v-col cols="1">
              <v-checkbox
                v-model="acknowledgePerformed"
                :id="testId + '.performedCheckbox'"
                hide-details
              />
            </v-col>
            <v-col cols="11">
              <span style="display: inline-block;" class="ml-md-n4 ml-xl-n16">
                A non-informative microarray has been performed (item #73292)
                for this patient. If you want to order a Microarray, please
                remove this test and choose 'Microarray' from the test options.
              </span>
            </v-col>
          </v-row>
          <v-row
            class="red--text"
            style="margin-top: -4px;"
            v-if="!acknowledgeCriteria || !acknowledgePerformed"
          >
            <v-col class="custom-col">
              Clinical criteria must be acknowledged
            </v-col>
          </v-row>

          <v-row v-if="!isMedicareEligible">
            <v-col class="custom-col">
              <v-alert
                text
                type="error"
                outlined
                icon="mdi-cash-usd-outline"
                class="ml-0"
              >
                Form cannot be submitted. {{ medicareIneligibleAlert }}
              </v-alert>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title"
                >Do you have clinical geneticist approval?*</span
              >
              <v-select
                :disabled="fieldDisabled"
                v-model="cgApproved"
                outlined
                :items="yesNoOptions"
                item-text="key"
                item-value="value"
                hide-details="auto"
                :id="testId + '.cgApproved'"
                :rules="ruleValidBoolean"
              ></v-select>
            </v-col>
          </v-row>
          <v-row v-if="cgApproved">
            <v-col class="custom-col">
              <span class="text-title">Clinical geneticist details*</span>
              <v-text-field
                v-model="cgDetails"
                :id="testId + '.cgDetails'"
                outlined
                hide-details="auto"
                :rules="cgApproved ? ruleNotNull : []"
              ></v-text-field>
              <span class="text-hint"
                >Please provide geneticist name and date approved.</span
              >
            </v-col>
          </v-row>
          <v-row v-if="cgApproved === false">
            <v-col class="custom-col">
              <v-alert text color="warning" outlined>
                Detailed clinical information is required for the VCGS
                laboratory clinical geneticist to provide approval.
                <b
                  >Please specify the severity of ID/GDD and FSIQ if
                  relevant.</b
                >
                <br /><br />
                If the order is approved, you will receive a prompt regarding
                consent.
              </v-alert>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family type*</span>
              <v-select
                :disabled="fieldDisabled"
                v-model="familyType"
                :items="familyTypes"
                :id="testId + '.familyType'"
                :rules="ruleNotNull"
                outlined
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>
          <v-row v-if="isTrio">
            <v-col class="custom-col">
              <v-checkbox
                id="medicare-trio-checkbox"
                v-model="medicareTrioCheckbox"
                :label="
                  `Trio exome requested. Singleton testing inappropriate, both
                biological parents available.`
                "
                class="shrink mr-2 form-no-border"
              >
              </v-checkbox>
              <v-alert v-if="!medicareTrioCheckbox" class="red--text my-1">
                Please acknowledge Medicare Trio requirements
              </v-alert>
            </v-col>
          </v-row>

          <v-row v-if="familyType === 'Singleton'">
            <v-col class="custom-col">
              <span class="text-title"
                >As a Singleton has been selected, please indicate why both
                biological parents are not available.</span
              >
              <v-textarea
                rows="2"
                auto-grow
                v-model="parentsAvailability"
                :id="testId + '.parentsAvailability'"
                outlined
                hide-details="auto"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <b class="text-title">Clinical features</b>
              <template v-if="brokenPhenotypesApi">
                <BrokenApiAlert type="phenotypes" />
              </template>
              <template v-else>
                <v-autocomplete
                  :disabled="fieldDisabled"
                  v-model="phenotypes"
                  :id="testId + '.phenotypes'"
                  :items="phenotypeList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  deletable-chips
                  @input="searchInputPhenotypes = null"
                  :search-input.sync="searchInputPhenotypes"
                  hide-details="auto"
                  outlined
                  @update:search-input="handlePhSearchChange"
                  :filter="filterPhItems"
                >
                  <template v-slot:prepend-item v-if="isSearchingHp">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ phSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please start typing the main clinical features. e.g.
                  intellectual disability. Select the most appropriate HPO term
                  from those suggested. Detailed clinical information helps us
                  diagnose patients more accurately. The phenotype's are sourced
                  from hpo.jax.org.</span
                >
              </template>
              <template v-if="badSearchQuery">
                <BadSearchQueryAlert />
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title"
                >Clinical notes & microarray results</span
              >
              <v-textarea
                :disabled="fieldDisabled"
                rows="2"
                auto-grow
                v-model="clinicalNotes"
                :id="testId + '.clinicalNotes'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. results of relevant prior genetic and non-genetic
                investigations, biochemistry, histopathology and imaging,
                etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family history</span>
              <v-textarea
                :disabled="fieldDisabled"
                rows="1"
                auto-grow
                v-model="familyHx"
                :id="testId + '.familyHx'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. affected status of the parents, parental consanguinity,
                etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <b class="text-title">Gene panels</b>
              <template v-if="brokenGenePanelsApi">
                <BrokenApiAlert type="genePanels" />
              </template>
              <template v-else>
                <v-autocomplete
                  :disabled="fieldDisabled"
                  v-model="genePanels"
                  :id="testId + '.genePanels'"
                  :items="genePanelsList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  item-color="blue"
                  deletable-chips
                  @input="searchInputPanels = null"
                  :search-input.sync="searchInputPanels"
                  outlined
                  hide-details="auto"
                  @update:search-input="handleGenePanelsSearchChange"
                  :filter="filterGenePanelsItems"
                >
                  <template v-slot:prepend-item v-if="isSearchingGenePanels">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ genePanelsSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please choose the most relevant gene panels to be used in
                  analysis. You can search panels using a key word. e.g.
                  congenital. The list will refine based on the word
                  entered.</span
                >
              </template>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <v-alert text color="info" outlined class="pb-0 mb-0">
                <p>
                  Detailed information on panel content is available at
                  <a href="https://panelapp-aus.org/" target="_blank"
                    ><b style="color: #00538b">PanelApp Australia</b></a
                  >
                </p>
              </v-alert>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <FamilyMember
                v-if="isTrio"
                title="Family member 1"
                :idPrefix="testId + '.fm0.'"
                ref="familyMember1"
                :patientdata="patientdata"
                :familytype="familyType"
                :populatekeys="[
                  'surname',
                  'country',
                  'unitFlat',
                  'streetAddress',
                  'suburb',
                  'postcode',
                  'state',
                ]"
                testType="childhood_syndromes"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <FamilyMember
                v-if="isTrio"
                title="Family member 2"
                :idPrefix="testId + '.fm1.'"
                ref="familyMember2"
                :patientdata="patientdata"
                :familytype="familyType"
                :populatekeys="[
                  'surname',
                  'country',
                  'unitFlat',
                  'streetAddress',
                  'suburb',
                  'postcode',
                  'state',
                ]"
                testType="childhood_syndromes"
              />
            </v-col>
          </v-row>
        </v-container>

        <ChildhoodSyndromesConsentContainer
          v-if="checkedAcknowledgement"
          ref="childhoodSyndromesConsent"
          :testName="testName"
          :testId="testId"
          :is-trio="isTrio"
          :force-form-dirty="forceFormDirty"
        />

        <TestPriceContainer
          :price="price"
          :showReanalysisAlert="showReanalysisAlert"
        />

        <v-btn
          class="primary-btn btn-padding-medium top-spaced-item"
          id="childhood_syndromes_continue"
          @click="goToNextSection()"
        >
          Continue to next section
        </v-btn>
      </v-form>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FamilyMember from "../FamilyMember";
import GenePanelMixin from "../../mixins/GenePanelMixin";
import PhenotypesMixin from "../../mixins/PhenotypesMixin";
import FormRulesMixin from "../../mixins/FormRulesMixin";
import { EventBus } from "@/event-bus";
import { useSectionStore } from "../../store/SectionStore";
import TestPriceContainer from "../common/TestPriceContainer.vue";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import UploadMixin from "../../mixins/UploadMixin.vue";
import ChildhoodSyndromesConsentContainer from "./ChildhoodSyndromesConsentContainer.vue";
import BrokenApiAlert from "../common/BrokenApiAlert.vue";
import BadSearchQueryAlert from "../common/BadSearchQueryAlert.vue";

const FAMILY_TYPES = ["Singleton", "Trio"];
const MEDICARE = true;

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
    showReanalysisAlert: Boolean,
  },

  mixins: [
    GenePanelMixin,
    FormRulesMixin,
    PhenotypesMixin,
    RetrieveJSONMixin,
    UploadMixin,
  ],

  components: {
    FamilyMember,
    TestPriceContainer,
    ChildhoodSyndromesConsentContainer,
    BrokenApiAlert,
    BadSearchQueryAlert,
  },
  watch: {
    price: function() {
      this.$emit("toggleTotalPrice", this.price, this.testId);
    },
    familyType: function(newVal) {
      // When it is trio test
      if (this.isTrio) {
        // get patient data via EventBus
        EventBus.$emit("getPatientData");

        // update selectedTrioTests in App component
        EventBus.$emit("selectTrioTest", this.testName);
      } else {
        // when it is singleton test, update selectedTrioTests in App component
        EventBus.$emit("deselectTrioTest", this.testName);
      }

      const pricingDetails = this.childhoodSyndromesTestPricing[this.urgency][
        newVal
      ][this.analysisType];

      // Update quotedPrice and totalPrice
      this.price = pricingDetails.price;
      this.$emit("toggleTotalPrice", this.price, this.testId);

      // Update testCode
      this.testCode = pricingDetails.testCode;
    },
  },
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.childhoodSyndromesTestPricing = await this.execRetrieveJson(
      "json/pricing/childhood_syndromes_pricing.json",
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    EventBus.$emit("getPatientData");
    const element = document.getElementById("childhood-syndromes-test-title");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }

    // Emitting this event is to set medicare details fields as required in Patient details screen
    EventBus.$emit("medicareChildhoodSyndromesStatus", true);
  },
  beforeDestroy() {
    EventBus.$emit("deselectTrioTest", this.testName);
  },
  data: () => ({
    valid: false,
    urgent: false,
    urgentReason: "",
    childhoodSyndromesTestPricing: null,
    price: null,
    suspectDiagnosis: "",
    congenital: false,
    cgApproved: null,
    cgDetails: "",
    urgencyTypes: [],
    genePanels: [],
    phenotypes: [],
    familyHx: "",
    clinicalNotes: "",
    urgency: "Standard",
    urgencyDisabled: false,
    familyType: "",
    familyTypes: FAMILY_TYPES,
    parentsAvailability: "",
    analysisType: "Full Exome (Mendeliome)",
    billTo: "MEDICARE",
    yesNoOptions: [
      { key: "Yes", value: true },
      { key: "No", value: false },
    ],
    searchInputPanels: null,
    searchInputGenes: null,
    searchInputPhenotypes: null,
    acknowledgeCriteria: false,
    acknowledgePerformed: false,
    medicareTrioCheckbox: false,
    medicare: MEDICARE,
    testCode: "",
  }),

  computed: {
    fieldDisabled() {
      return !this.checkedAcknowledgement || !this.isMedicareEligible;
    },
    patientAge() {
      return this.patientdata["age"];
    },
    patientHospitalStatus() {
      return this.patientdata["patientStatusContent"];
    },
    // Check if the patient is eligible for mediare in the case of Childhood syndromes test
    isMedicareEligible() {
      if (
        this.patientHospitalStatus === "public_public_hospital" ||
        this.patientHospitalStatus === "public_outpatient_public_hospital"
      ) {
        return false;
      }

      /** This case only happens in the local environment when patient screen has prefilled data.
       * This is because DOB fields' @change events in PatientDetails will not be triggered and as a result `calculateAge` method
       * won't be triggered. This leads to patientAge to be `null`
       */
      if (this.patientAge === null || this.patientAge < 0) {
        return false;
      }

      /** NB: with prefilled data for DOB in local environment, it won't fall into this IF statement unless we re-edit DOB data.
      /* This is because when DOB fields have prefilled data their @change events in PatientDetails will not be triggered and as a result `calculateAge` method
       * won't be triggered. This leads to patientAge to be `null`
       */
      if (this.patientAge >= 11) {
        return false;
      }

      return true;
    },
    // Check whether to display medicare ineligible alert
    medicareIneligibleAlert() {
      if (
        this.patientHospitalStatus === "public_public_hospital" ||
        this.patientHospitalStatus === "public_outpatient_public_hospital"
      ) {
        return "You cannot order a Medicare test for a public patient. If this patient is not eligible for Medicare testing, please contact VCGSGenomics to discuss the test options available.";
      }

      if (this.patientAge === null || this.patientAge < 0) {
        return "Please enter a valid date of birth for the patient to determine if they qualify for Medicare. If this patient is not eligible for Medicare testing, please contact VCGS Genomics to discuss the test options available.";
      }

      if (this.patientAge >= 11) {
        return "The age of the patient must be less than 11 years to qualify for Medicare. If this patient is not eligible for Medicare testing, please contact VCGSGenomics to discuss the test options available.";
      }

      return null;
    },
    checkedAcknowledgement() {
      return this.acknowledgeCriteria && this.acknowledgePerformed;
    },
    isTrio() {
      return this.familyType === "Trio";
    },
    medicareNoCgApproved() {
      return this.billTo === "MEDICARE" && this.cgApproved === false;
    },
    familyMemberFormsValidated() {
      if (this.$refs.familyMember1 && this.$refs.familyMember2) {
        return (
          this.$refs.familyMember1.isFormValid() &&
          this.$refs.familyMember2.isFormValid()
        );
      }

      return false;
    },
  },
  methods: {
    validateConsentForm: function() {
      if (typeof this.$refs.childhoodSyndromesConsent !== "undefined") {
        return this.$refs.childhoodSyndromesConsent.isFormValid();
      }

      return false;
    },
    isFormValid: function() {
      const consentFormValidated = this.validateConsentForm();

      if (this.cgApproved) {
        // If it is singleton test, only validate consent form
        if (!this.isTrio) return consentFormValidated;

        // If it is trio test, validate consent form, medicareTrioCheckbox and family member forms
        return (
          consentFormValidated &&
          this.medicareTrioCheckbox &&
          this.familyMemberFormsValidated
        );
      } else {
        /** If the test is not cg approved - it is not really a test order at all and it just need to fill in some info for follow-up
         * In this case, we can skip validating consent form for both singleton and trio
         */
        /** If it is singleton test, we do not need to validate anything */
        if (!this.isTrio) return true;

        // If it is trio test, validate medicareTrioCheckbox and family member forms
        return this.medicareTrioCheckbox && this.familyMemberFormsValidated;
      }
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    transferData: function() {
      let familyDetails = [];
      //Ordering family member in the array so it stays consistantly [fm0, fm1]
      if (this.familyType === "Trio") {
        const orderEntries = Object.entries(this.$refs)
          .filter(
            ([familyKey]) => familyKey.substring(0, 12) === "familyMember",
          )
          .sort();
        for (let [, familyComponent] of orderEntries) {
          familyDetails.push(familyComponent.transferData());
        }
      }

      // If `parentsAvailability` exists, add it as a seperate line in the top of `familyHx` string
      // It will be saved in the FAMILY_HX col in the db
      let newFamilyHx = this.parentsAvailability
        ? `Biological parents availability: ${this.parentsAvailability}\n${this.familyHx}`
        : this.familyHx;

      let result = {
        urgent: this.urgent,
        urgentReason: this.urgentReason,
        testType: this.testId,
        familyType: this.familyType,
        analysisType: this.analysisType,
        medicareTrioCheckbox: this.medicareTrioCheckbox,
        genePanels: this.getFinalGenePanels(this.genePanels),
        phenotypes: this.getFinalPhenotypes(this.phenotypes),
        familyHx: newFamilyHx,
        clinicalNotes: this.clinicalNotes,
        family: familyDetails,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        medicare: this.medicare,
        cgApproved: this.cgApproved,
        cgDetails: this.cgDetails,
        billTo: this.billTo,
      };

      // Consent data is present, except if the test is not approved by gc
      if (this.$refs.childhoodSyndromesConsent) {
        const consentData = this.$refs.childhoodSyndromesConsent.transferData();

        // assign the data fields from consent direct into the top level of our data
        Object.assign(result, consentData);
      }

      return result;
    },
  },
};
</script>
<style scoped></style>
